import React, { useEffect, useState } from 'react'
import { Button, Stack, Typography } from '@mui/material'
import { apiGet } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import SmartSelect from '../../generic/smartSelect/SmartSelect'
import SmartTextField from '../../generic/smarttextfield/SmartTextField'
import DocumentRepositoryRecordPicker from './DocumentRepositoryRecordPicker'
import { documentRepositoryRecordSearchRequest, searchDocumentRepository, uploadDocumentRepositoryDocument } from './documentRepositoryApi'
import { extractFileNameParts, parseMaxFileSizeBytes, validateUploadCandidate } from './documentRepositoryUploadValidation'

/**
 * DocumentRepositoryUploadDialog — historia 170, HU-03 (J3, CU5). RN21 (DI-3): temática (solo ADD +
 * recordSearch, ya filtradas por el llamante en `uploadableTopics`) + registro (elegido con
 * `DocumentRepositoryRecordPicker`, mismo puerto R3 que el filtro de J2) + UN fichero (DG-16) + ficha.
 * V8 (`documentRepositoryUploadValidation.js`) bloquea el botón de confirmar mientras el candidato no
 * sea válido — nunca se llega a construir la petición R6 con un candidato inválido (S75).
 *
 * `file` is `''` (never `null`, CLAUDE.md) while nothing is selected — same "absent" sentinel as
 * every other business field in this store; a real selection is a `File` object, always truthy, so
 * every check below (`file ? ... : ...`) works unchanged either way.
 */
const DocumentRepositoryUploadDialog = ({ uploadableTopics, documentTypes, onClose, onUploaded, onError }) => {
  const { t_ } = useSmartTranslation()
  const [selectedTopic, setSelectedTopic] = useState('')
  const [selectedReferenceId, setSelectedReferenceId] = useState('')
  const [file, setFile] = useState('')
  const [documentName, setDocumentName] = useState('')
  const [documentTypeId, setDocumentTypeId] = useState('')
  const [description, setDescription] = useState('')
  const [maxFileSizeBytes, setMaxFileSizeBytes] = useState(parseMaxFileSizeBytes(''))
  const [existingDocuments, setExistingDocuments] = useState([])

  useEffect(() => {
    let cancelled = false
    // `response.value` is the ONE place this component reads a value straight from the API without
    // normalizing it first — `parseMaxFileSizeBytes` is the single function allowed to compare it
    // against `null`/`undefined` (documentRepositoryUploadValidation.js), so it is passed through as-is.
    apiGet('settings/MAX_FILE_SIZE').then((response) => {
      if (!cancelled) setMaxFileSizeBytes(parseMaxFileSizeBytes(response ? response.value : ''))
    }).catch(() => { if (!cancelled) setMaxFileSizeBytes(parseMaxFileSizeBytes('')) })
    return () => { cancelled = true }
  }, [])

  // S75 (last example): "el mismo nombre y el mismo fichero que un documento ya asociado a REF-100",
  // consultado con R2 filtrado por ese registro — refetched every time the chosen record changes.
  useEffect(() => {
    let cancelled = false
    if (!selectedReferenceId) {
      setExistingDocuments([])
      return
    }
    searchDocumentRepository(documentRepositoryRecordSearchRequest(selectedTopic, selectedReferenceId, 100))
      .then((response) => { if (!cancelled) setExistingDocuments((response && response.content) || []) })
      .catch(() => { if (!cancelled) setExistingDocuments([]) })
    return () => { cancelled = true }
  }, [selectedTopic, selectedReferenceId])

  const handleRecordSelected = (topic, referenceId) => {
    setSelectedTopic(topic)
    setSelectedReferenceId(referenceId)
  }

  const handleFileChange = (event) => {
    const selected = event.target.files && event.target.files[0] ? event.target.files[0] : ''
    setFile(selected)
    if (selected) setDocumentName(extractFileNameParts(selected.name).name)
  }

  const validation = validateUploadCandidate({
    fileName: file ? file.name : '',
    fileSize: file ? file.size : 0,
    documentName,
    documentTypeId,
    maxFileSizeBytes,
    existingDocuments
  })
  const canSend = Boolean(selectedReferenceId) && validation.valid

  const handleAccept = async () => {
    if (!canSend) return
    try {
      await uploadDocumentRepositoryDocument(selectedTopic, selectedReferenceId, {
        file, documentName, documentType: documentTypeId, description
      })
      onUploaded()
    } catch (error) {
      onError(error && error.message ? error.message : '')
    }
  }

  return (
    <SmartDialog
      setOpen
      title={createDialogTitle({ title: t_('Upload document') })}
      renderComponent={(
        <Stack spacing={2} sx={{ minWidth: '24rem' }}>
          <DocumentRepositoryRecordPicker topics={uploadableTopics} onSelect={handleRecordSelected} />

          <Button component='label'>
            {t_('Upload document')}
            <input
              type='file'
              hidden
              data-testid='document-repository-upload-file-input'
              onChange={handleFileChange}
            />
          </Button>
          {file && <Typography variant='caption'>{file.name}</Typography>}

          <SmartTextField
            label={t_('Document name')}
            value={documentName}
            onChange={setDocumentName}
          />
          <SmartSelect
            label={t_('Document type')}
            autoSelectFirst={false}
            selectableOptions={documentTypes.map((type) => String(type.id))}
            value={documentTypeId === '' ? null : String(documentTypeId)}
            optionLabelTransform={(idString) => (documentTypes.find((type) => String(type.id) === idString) || {}).name || idString}
            onChange={(idString) => setDocumentTypeId(idString ? Number(idString) : '')}
          />
          <SmartTextField
            label={t_('Description')}
            value={description}
            onChange={setDescription}
            multiline
          />
        </Stack>
      )}
      disableAccept={!canSend}
      acceptCallback={handleAccept}
      cancelCallback={onClose}
      closeCallback={onClose}
    />
  )
}

export default DocumentRepositoryUploadDialog
